import type { PortalForecastRow } from '@proerp/shared';

/**
 * Anzeige-Aufbereitung der Bedarfsvorschau. Reine Funktionen — die
 * Gruppierung nach Termin ist die eigentliche Aussage der Seite und soll
 * ohne DOM prüfbar sein.
 */

/** Ein Termin mit den Positionen, die zu ihm gehören. */
export interface ForecastGroup {
  /** ISO-Termin oder `null` für „noch ohne Termin". */
  expectedDate: string | null;
  rows: PortalForecastRow[];
}

/**
 * Monats-Schlüssel eines Termins (`2026-08`) bzw. `null` ohne Termin. Die
 * Gruppierung MUSS dieselbe Einheit verwenden wie die Überschrift
 * (`formatExpectedDate` zeigt Monat + Jahr) — sonst entstehen mehrere Gruppen
 * mit derselben Überschrift.
 */
function monthKey(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  // Europe/Berlin, weil die Überschrift in derselben Zone formatiert wird —
  // sonst rutscht ein Termin vom 1. um 00:00 UTC in den Vormonat.
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    timeZone: 'Europe/Berlin',
  }).format(date);
  return parts.slice(0, 7);
}

/**
 * Gruppiert die Zeilen nach **Monat** — der Einheit, in der die Seite den Termin
 * auch anzeigt. Vorher wurde nach dem exakten Zeitstempel gruppiert: Zwei
 * Positionen im selben Monat (3. und 17.) ergaben zwei Gruppen mit identischer
 * Überschrift „August 2026" — auf dem Telefon zwei getrennte Blöcke für etwas,
 * das der Lieferant als einen Monat liest (Befund Mobil-Audit 2026-08-10).
 *
 * Die API liefert bereits nach Termin sortiert (Zeilen ohne Termin am Ende) —
 * hier wird nur zusammengefasst, die Reihenfolge bleibt wie geliefert. So
 * bestimmt eine Stelle die Sortierung, nicht zwei.
 */
export function groupByExpectedDate(rows: readonly PortalForecastRow[]): ForecastGroup[] {
  const groups: (ForecastGroup & { key: string | null })[] = [];
  for (const row of rows) {
    const key = monthKey(row.expectedDate);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.rows.push(row);
      continue;
    }
    // `expectedDate` der Gruppe = Termin der ERSTEN Zeile. Er dient nur der
    // Überschrift; innerhalb der Gruppe ist der Monat ohnehin derselbe.
    groups.push({ key, expectedDate: row.expectedDate, rows: [row] });
  }
  return groups.map(({ key: _key, ...group }) => group);
}

/**
 * Termin für die Anzeige. Bewusst mit Monatsnamen statt „01.03.2026": Eine
 * Vorschau ist keine Zusage auf den Tag genau, und ein tagesgenaues Datum
 * würde beim Lieferanten mehr Verbindlichkeit suggerieren, als gemeint ist.
 */
export function formatExpectedDate(iso: string | null): string {
  if (!iso) return 'Noch ohne Termin';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Noch ohne Termin';
  return date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: 'Europe/Berlin' });
}

/** Menge in deutscher Schreibweise, ohne unnötige Nachkommastellen. */
export function formatQuantity(quantity: number): string {
  return quantity.toLocaleString('de-DE', { maximumFractionDigits: 3 });
}
